import { useState } from 'react';
import { useNavigate } from 'react-router';
import { ArrowLeft, Loader2, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAdminTrashContent, useRestoreContent } from '@/hooks/use-content';
import { toToastMessage } from '@/lib/api-error';
import {
  contentStatusLabels,
  contentTypeLabels,
  type ContentItem,
} from '@/types/content';

// SPEC-021: Admin-only trash view. Soft-deleted content items live here
// and can be restored back into their previous status with one click.

const PER_PAGE = 20;

export default function ContentTrashPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const offset = (page - 1) * PER_PAGE;
  const { data, isLoading } = useAdminTrashContent({ limit: PER_PAGE, offset });
  const restore = useRestoreContent();
  const items = data?.data ?? [];

  const handleRestore = (item: ContentItem) => {
    if (!window.confirm(`Restore "${item.title}"?`)) return;
    restore.mutate(item.id, {
      onSuccess: () => toast.success(`Restored "${item.title}"`),
      onError: (e) => toast.error(toToastMessage(e, 'Restore failed')),
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/content')}>
          <ArrowLeft className="mr-1 h-4 w-4" />
          Back
        </Button>
        <h1 className="text-2xl font-semibold">Content trash</h1>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">
            Soft-deleted items ({data?.pagination?.total ?? 0})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center gap-2 p-8 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading…
            </div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              Trash is empty.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status at deletion</TableHead>
                  <TableHead>Deleted</TableHead>
                  <TableHead className="w-24 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="max-w-[320px]">
                      <p className="truncate font-medium">{item.title}</p>
                      {item.summary && (
                        <p className="truncate text-xs text-muted-foreground">
                          {item.summary}
                        </p>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {contentTypeLabels[item.content_type]}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {contentStatusLabels[item.status]}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {item.deleted_at
                        ? new Date(item.deleted_at).toLocaleString()
                        : '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleRestore(item)}
                        disabled={restore.isPending}
                      >
                        <Undo2 className="mr-1 h-4 w-4" />
                        Restore
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {data?.pagination && data.pagination.total_pages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={page === 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Previous
          </Button>
          <span className="text-xs text-muted-foreground">
            Page {page} of {data.pagination.total_pages}
          </span>
          <Button
            size="sm"
            variant="outline"
            disabled={page >= data.pagination.total_pages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
